import { useState, useEffect, useCallback, useRef } from 'react';
import { getContext } from '@/context';

import type { SDKContextExtended } from '@/types';

export const useOpen = (onOpen?: () => void): boolean => {
  const [isOpen, setIsOpen] = useState(false);
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    if (onOpenRef.current) {
      onOpenRef.current();
    }
  }, []);

  useEffect(() => {
    try {
      const context = getContext() as unknown as SDKContextExtended;

      // Try different ways to subscribe to open event based on SDK version
      let unsubscribe: (() => void) | undefined;

      if (context.lifecycle?.open?.subscribe) {
        unsubscribe = context.lifecycle.open.subscribe(handleOpen);
      } else if (context.handlers?.onOpen) {
        unsubscribe = context.handlers.onOpen(handleOpen);
      } else {
        // Fallback: assume already open
        setIsOpen(true);
      }

      return () => {
        unsubscribe?.();
      };
    } catch {
      setIsOpen(true);
    }
  }, [handleOpen]);

  return isOpen;
};

export default useOpen;
